import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Grid,
  GridColumn,
  GridNoRecords,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import {downloadIcon, pencilIcon, plusIcon} from '@progress/kendo-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import moment from 'moment';
import {
  ICustomer,
  ILocationFilter,
  ILocationGridItem,
  ILocationItem,
  LocationType,
  LocationTypeLabels
} from '../../model';
import { useExtendedLocalization, useFormatter } from '../../hooks';
import { CSVLink } from 'react-csv';
import {locationsService, notifications} from '../../services';
import {usePermissions} from "../../components/auth/PermissionsProvider";
import styles from "./Locations.module.scss";
import {debounceTime, Subject} from "rxjs";
import {TextBox} from "@progress/kendo-react-inputs";
import { SortDescriptor } from '@progress/kendo-data-query';
import {hasUmojoAnalytics} from "../../model/api/nexParkInstance/instancesChecker";
import {useGridColumnPersist} from "../../hooks/useGridColumnPersist";

interface ILocationsState {
  data: ILocationItem[];
  total: number;
}

interface IProps {
  forCustomer: boolean;
  customer: ICustomer;
}
export enum LocationGridColumn {
  Name = 'name',
  CustomerName = 'customer.name',
  Address = 'address',
  Type = 'type',
  Capacity = 'capacity',
  Enabled = 'enabled',
  UpdatedAt = 'updatedAt',
  Actions = 'actions',
}

const defaultPaginator = {
  skip: 0,
  take: 20,
  sort: [] as SortDescriptor[],
};
export const LocationsGrid: FC<IProps> = ({ forCustomer, customer }) => {
  const localization = useExtendedLocalization();
  const navigate = useNavigate();
  const formatter = useFormatter();
  const { hasEditAccess, hasCSVExportAccess } = usePermissions();
  const [queryText, setQueryText] = useState<string>();
  const [filter, setFilter] = useState<ILocationFilter>({ ...defaultPaginator, customers: [customer.id] });
  const filterRef = useRef<ILocationFilter>();
  filterRef.current = filter;

  const baseColumns = useMemo(() => [
    { field: LocationGridColumn.Name, title: "Name", minWidth: 150 },
    { field: LocationGridColumn.CustomerName, title: "Customer", hidden: forCustomer, minWidth: 150 },
    { field: LocationGridColumn.Address, title: "Address", minWidth: 200 },
    { field: LocationGridColumn.Type, title: "Type", minWidth: 120 },
    { field: LocationGridColumn.Capacity, title: "Capacity", minWidth: 100 },
    { field: LocationGridColumn.Enabled, title: "Status", minWidth: 100 },
    { field: LocationGridColumn.UpdatedAt, title: "Last Update", minWidth: 150 },
    { field: LocationGridColumn.Actions, title: "Actions", width: 80, resizable: false, reorderable: false, sortable: false }
  ], [forCustomer]);

  const { columns, onColumnResize, onColumnReorder } = useGridColumnPersist(
      `customer-locations-grid-order-and-size`,
      baseColumns
  );

  const onSearch$ = useMemo(() => new Subject<string | undefined>(), []);
  useEffect(() => {
    const subscription = onSearch$.pipe(debounceTime(800)).subscribe((x) => {
      setFilter({ ...filterRef.current, ...defaultPaginator, query: x });
    });
    return () => subscription.unsubscribe();
  }, [onSearch$]);

  const hasUmojoAnalyticsConnected = hasUmojoAnalytics(customer);

  const [locationsState, setLocationsState] = useState<ILocationsState>({
    data: [],
    total: 0,
  });

  useEffect(() => {
    const dataState: any = {
      skip: filter.skip,
      take: filter.take,
      sort: filter.sort,
      filter: {
        logic: 'and',
        filters: [
          { field: 'customerId', operator: 'eq', value: customer.id }
        ]
      }
    };

    locationsService.getListForGrid(dataState, filter.query)
        .then((res) => {
          setLocationsState({ data: res.data as unknown as ILocationItem[], total: res.total });
        })
        .catch(err => {
          notifications.error('Failed to load locations');
          console.error(err)
        });

  }, [filter, customer.id]);


  const csvLinkRef = useRef<any>(null);
  const [allCsvData, setAllCsvData] = useState<any[]>([]);
  const [isCsvLoading, setIsCsvLoading] = useState(false);

  const csvHeaders = useMemo(() => {
    const headers = [
      { label: 'Name', key: 'Name' },
      { label: 'Address', key: 'Address' },
      { label: 'Type', key: 'Type' },
      { label: 'Capacity', key: 'Capacity' },
      { label: 'Status', key: 'Status' },
      { label: 'Last Update', key: 'Last Update' },
    ];

    if (!forCustomer) {
      headers.splice(1, 0, { label: 'Customer', key: 'Customer' });
    }

    return headers;
  }, [forCustomer]);

  const getFileName = () => {
    return `${customer.name}_${moment().format('YYYYMMDD_HHmm')}.csv`;
  };

  const loadAllDataForCsv = useCallback(async () => {
    setIsCsvLoading(true);
    try {
      const allDataState: any = {
        skip: 0,
        take: locationsState.total,
        sort: filter.sort,
        filter: {
          logic: 'and',
          filters: [
            { field: 'customerId', operator: 'eq', value: customer.id }
          ]
        }
      };

      const result = await locationsService.getListForGrid(allDataState, filter.query);

      const formattedData = result.data.map((item: ILocationGridItem) => {
        const row: any = {
          Name: item.name,
          Address: item.address,
          Type: localization.toLanguageStringF(`location.types.${item.type}`) || LocationTypeLabels[item.type as LocationType],
          Capacity: item.capacity,
          Status: item.enabled ? 'Active' : 'Inactive',
          'Last Update': moment(item.updatedAt).format('MM/DD/YYYY h:mma'),
        };

        if (!forCustomer) {
          row.Customer = item.customerName || item?.customer?.name || '';
        }

        return row;
      });

      setAllCsvData(formattedData);
    } catch (error) {
      notifications.error('Failed to load data for CSV export');
    } finally {
      setIsCsvLoading(false);
    }
  }, [filter, locationsState.total, forCustomer, localization, customer.id]);

  const handleCsvExport = useCallback(() => {
    if (allCsvData.length === 0) {
      loadAllDataForCsv().then(() => {
        setTimeout(() => {
          csvLinkRef.current?.link.click();
        }, 100);
      });
    } else {
      csvLinkRef.current?.link.click();
    }
  }, [allCsvData, loadAllDataForCsv]);

  useEffect(() => {
    setAllCsvData([]);
  }, [filter]);

  const handlePageChange = useCallback((event: any) => {
    let newSkip = event.page.skip;
    let newTake = event.page.take;

    if (event.targetEvent?.value === 'All' || newTake === 'All') {
      newTake = locationsState.total;
      newSkip = 0;
    }

    setFilter({
      ...filter,
      skip: newSkip,
      take: newTake
    });
  }, [filter, locationsState.total]);

  const onSortChange = (event: GridSortChangeEvent) => {
    setFilter({
      ...filter,
      sort: event.sort,
      skip: 0,
    });
  };

  const gridKey = useMemo(() =>
          columns.map(c => c.field).join('_'),
      [columns]);

  return (
      <>
        <div className={styles.filterPanel}>
          <div className={styles.search}>
            <TextBox
                value={queryText ?? ''}
                onChange={(x) => {
                  const val = x.value?.toString();
                  setQueryText(val);
                  onSearch$.next(val);
                }}
                placeholder='Search'
            />
          </div>
          <div className={styles.buttonsPanel}>
            <CSVLink data={allCsvData} headers={csvHeaders} filename={getFileName()} ref={csvLinkRef} style={{ display: 'none' }} />
            {hasCSVExportAccess && <Button className={styles.basicButton} svgIcon={downloadIcon} onClick={handleCsvExport} disabled={isCsvLoading}>
              Export to CSV
            </Button>}
            {hasEditAccess && hasUmojoAnalyticsConnected && (
                <Button
                    className={styles.basicButton}
                    type='button'
                    svgIcon={plusIcon}
                    onClick={() => navigate('/locations/new', { state: { edit: true, customer: { id: customer.id, name: customer.name } } })}
                >
                  Add Location
                </Button>
            )}
          </div>
        </div>
        <Grid
            key={gridKey}
            reorderable={true}
            resizable={true}
            sortable={true}
            data={locationsState.data}
            total={locationsState.total}
            skip={filter.skip}
            take={filter.take}
            sort={filter.sort}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            onSortChange={onSortChange}
            onPageChange={handlePageChange}
            pageable={{
              buttonCount: 4,
              info: true,
              type: 'numeric',
              pageSizes: [10, 20, 50, 'All'],
              previousNext: true,
            }}
        >
          <GridNoRecords>
            <div>
            There are no locations added to the list.{' '}
            {hasEditAccess && hasUmojoAnalyticsConnected && (
                <>
                  Click{' '}
                  <Link
                      to="/locations/new"
                      state={{ edit: true }}
                  >
                    here
                  </Link>{' '}
                  to add one.
                </>
            )}
            </div>
          </GridNoRecords>
          {columns.map((col: any, index: number) => {
            if (col.hidden) return null;

            const columnKey = col.field;
            switch (col.field) {
              case LocationGridColumn.Name: {
                return (
                    <GridColumn
                        key={columnKey} {...col}
                        cell={(props) => (
                            <td>
                              <Link to={`/locations/${props.dataItem.id}/details`}>{props.dataItem.name}</Link>
                            </td>
                        )}
                    />
                );
              }
              case LocationGridColumn.CustomerName: {
                return (
                    <GridColumn
                        key={columnKey} {...col}
                        cell={(props) => (
                            <td>
                              <Link to={`/customers/${props.dataItem.customer.id}`}>{props.dataItem.customer.name}</Link>
                            </td>
                        )}
                    />
                );
              }
              case LocationGridColumn.Type: {
                return (
                    <GridColumn
                        key={columnKey} {...col}
                        cell={(props) => <td>{localization.toLanguageStringF(`location.types.${props.dataItem.type}`)}</td>}
                    />
                );
              }
              case LocationGridColumn.Enabled: {
                return (
                    <GridColumn
                        key={columnKey} {...col}
                        cell={(props) => <td>{props.dataItem.enabled ? 'Active' : 'Inactive'}</td>}
                    />
                );
              }
              case LocationGridColumn.UpdatedAt: {
                return (
                    <GridColumn
                        key={columnKey} {...col}
                        cell={(props) => <td>{formatter.formatDate(props.dataItem.updatedAt)}</td>}
                    />
                );
              }
              case LocationGridColumn.Actions: {
                return hasEditAccess ? (
                    <GridColumn
                        style={{ cursor: 'default' }}
                        key={columnKey}
                        {...col}
                        cell={(props) => (
                            <td className='td-center'>
                              <Button svgIcon={pencilIcon} onClick={() => navigate(`/locations/${props.dataItem.id}/details`, {state: {edit: true}})} />
                            </td>
                        )}
                    />
                ) : null;
              }
              default:
                return <GridColumn key={columnKey} {...col} />;
            }
          })}
        </Grid>
      </>
  );
};
